<template>
  <div class="ai-assistant">
    <!-- AI图标按钮 -->
    <div 
      class="ai-icon-btn"
      @mouseenter="showDialog = true"
      @mouseleave="handleMouseLeave"
    >
      <img :src="require('@/assets/doubao_avatar.png')" class="ai-avatar" alt="AI助手" />
    </div>

    <!-- 对话窗口 -->
    <transition name="el-zoom-in-top">
      <div 
        v-show="showDialog" 
        class="ai-chat-dialog"
        @mouseenter="cancelHide"
        @mouseleave="handleMouseLeave"
      >
        <!-- 对话头部 -->
        <div class="chat-header">
          <span class="chat-title">🤖 AI助手</span>
          <i class="el-icon-close close-btn" @click="closeDialog"></i>
        </div>

        <!-- 对话内容区域 -->
        <div class="chat-body" ref="chatBody">
          <div v-if="messages.length === 0" class="empty-tip">
            <p>你好！我是AI助手，有什么可以帮助你的吗？</p>
          </div>
          
          <div 
            v-for="(msg, index) in messages" 
            :key="index" 
            class="message-item"
            :class="msg.type"
          >
            <div class="message-content">
              <div class="message-text">{{ msg.content }}</div>
              <div v-if="msg.error" class="message-error">{{ msg.error }}</div>
            </div>
          </div>

          <!-- 加载状态 -->
          <div v-if="loading" class="message-item ai">
            <div class="message-content">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- 输入区域 -->
        <div class="chat-footer">
          <el-input
            v-model="inputMessage"
            type="textarea"
            :rows="2"
            placeholder="输入消息，按Enter发送..."
            :disabled="loading"
            @keydown.enter.native="handleEnter"
            resize="none"
            class="chat-input"
          />
          <el-button
            type="primary"
            size="small"
            :loading="loading"
            :disabled="!inputMessage.trim()"
            @click="sendMessage"
            class="send-btn"
          >
            发送
          </el-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { aiChat } from '@/api/ai/chat'

export default {
  name: 'AiAssistant',
  data() {
    return {
      showDialog: false,
      messages: [],
      inputMessage: '',
      loading: false,
      hideTimer: null
    }
  },
  methods: {
    // 处理鼠标离开
    handleMouseLeave() {
      this.hideTimer = setTimeout(() => {
        this.showDialog = false
      }, 300)
    },
    
    // 取消隐藏
    cancelHide() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },
    
    // 关闭对话框
    closeDialog() {
      this.showDialog = false
      this.cancelHide()
    },
    
    // 处理回车键
    handleEnter(e) {
      if (!e.shiftKey) {
        e.preventDefault()
        this.sendMessage()
      }
    },
    
    // 发送消息
    async sendMessage() {
      const message = this.inputMessage.trim()
      if (!message || this.loading) return

      // 添加用户消息
      this.messages.push({
        type: 'user',
        content: message
      })

      // 清空输入框
      this.inputMessage = ''
      this.loading = true

      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      try {
        // 调用AI接口
        const res = await aiChat({ message })
        
        // res 为 { success: true, reply: "..." }
        if (res.success) {
          // 添加AI回复
          this.messages.push({
            type: 'ai',
            content: res.reply
          })
        } else {
          // 添加错误消息
          this.messages.push({
            type: 'ai',
            content: '抱歉，我遇到了一些问题。',
            error: res.errorMessage
          })
        }
      } catch (error) {
        console.error('AI对话失败:', error)
        this.messages.push({
          type: 'ai',
          content: '网络请求失败，请稍后重试。',
          error: error.message
        })
      } finally {
        this.loading = false
        
        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    
    // 滚动到底部
    scrollToBottom() {
      const chatBody = this.$refs.chatBody
      if (chatBody) {
        chatBody.scrollTop = chatBody.scrollHeight
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-assistant {
  position: relative;
  display: inline-block;
  line-height: 1;
}

.ai-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: scale(1.1);
  }
  
  .ai-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.ai-chat-dialog {
  position: absolute;
  top: 50px;
  right: 0;
  width: 380px;
  height: 500px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 9999;
  overflow: hidden;

  .chat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    flex-shrink: 0;

    .chat-title {
      font-size: 16px;
      font-weight: 600;
    }

    .close-btn {
      font-size: 18px;
      cursor: pointer;
      transition: transform 0.2s;

      &:hover {
        transform: rotate(90deg);
      }
    }
  }

  .chat-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #f5f7fa;

    .empty-tip {
      text-align: center;
      color: #909399;
      margin-top: 100px;
      
      p {
        font-size: 14px;
      }
    }

    .message-item {
      margin-bottom: 16px;
      display: flex;

      &.user {
        justify-content: flex-end;

        .message-content {
          background: #409EFF;
          color: #fff;
          border-radius: 8px 8px 0 8px;
          max-width: 70%;
        }
      }

      &.ai {
        justify-content: flex-start;

        .message-content {
          background: #fff;
          color: #303133;
          border-radius: 8px 8px 8px 0;
          max-width: 70%;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
      }

      .message-content {
        padding: 10px 14px;
        word-break: break-word;

        .message-text {
          font-size: 14px;
          line-height: 1.6;
          white-space: pre-wrap;
        }

        .message-error {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid rgba(0, 0, 0, 0.1);
          font-size: 12px;
          color: #F56C6C;
        }
      }
    }

    // 打字指示器动画
    .typing-indicator {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 0;

      span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #909399;
        animation: typing 1.4s infinite;

        &:nth-child(2) {
          animation-delay: 0.2s;
        }

        &:nth-child(3) {
          animation-delay: 0.4s;
        }
      }
    }

    @keyframes typing {
      0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.7;
      }
      30% {
        transform: translateY(-10px);
        opacity: 1;
      }
    }
  }

  .chat-footer {
    padding: 12px 16px;
    background: #fff;
    border-top: 1px solid #EBEEF5;
    display: flex;
    gap: 8px;
    flex-shrink: 0;

    .chat-input {
      flex: 1;

      ::v-deep textarea {
        padding: 8px 12px;
        font-size: 14px;
      }
    }

    .send-btn {
      align-self: flex-end;
      min-width: 60px;
    }
  }
}

// 过渡动画
.el-zoom-in-top-enter-active,
.el-zoom-in-top-leave-active {
  opacity: 1;
  transform: scaleY(1);
  transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  transform-origin: center top;
}

.el-zoom-in-top-enter,
.el-zoom-in-top-leave-active {
  opacity: 0;
  transform: scaleY(0);
}
</style>
